import type { ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import BusinessIcon from '@mui/icons-material/Business'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import SettingsIcon from '@mui/icons-material/Settings'
import CategoryIcon from '@mui/icons-material/Category'
import LogoutIcon from '@mui/icons-material/Logout'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Tooltip from '@mui/material/Tooltip'
import { usePersona, type Persona } from '../PersonaContext'

const DRAWER_WIDTH = 260

interface AppShellProps {
  children: ReactNode
}

// `platformOnly` items are hidden in the RD-Admin preview — an RD Admin can't
// create orgs/RDs or edit the global registries. RD-scoped work (members,
// roles, policies, groups, agents, audit) is reached *through* an RD, so it
// stays available; in production that's the surface the Ros RD-Admin UI exposes.
const NAV_ITEMS = [
  { label: 'Organizations', path: '/orgs', icon: <BusinessIcon fontSize="small" />, platformOnly: true },
  { label: 'Resource Types', path: '/resource-types', icon: <CategoryIcon fontSize="small" />, platformOnly: true },
  { label: 'Setting Definitions', path: '/settings', icon: <SettingsIcon fontSize="small" />, platformOnly: true },
  { label: 'Check Tester', path: '/check', icon: <VerifiedUserIcon fontSize="small" />, platformOnly: false },
]

export function AppShell({ children }: AppShellProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { persona, setPersona, canPlatform, activeRdId } = usePersona()

  // The RD currently in view (from the URL) — used to keep RD-scoped nav and to
  // stay on the same RD when toggling persona while on an RD page.
  const rdMatch = location.pathname.match(/^\/rds\/([^/]+)/)
  const currentRdId = rdMatch?.[1] ?? activeRdId

  function handlePersonaChange(next: Persona) {
    setPersona(next)
    if (next === 'platform') {
      // leaving RD preview → go to the platform home unless already platform-scoped
      if (!location.pathname.startsWith('/rds/') && !location.pathname.startsWith('/check')) navigate('/orgs')
    } else {
      // entering RD preview → stay on the RD if we're on one, else pick an RD
      if (!location.pathname.startsWith('/rds/')) navigate('/rd-home')
    }
  }

  // The sidebar nav depends on persona:
  //  - Platform Admin: orgs + global registries + check tester.
  //  - RD Admin: the active RD's sections (Overview / Policy Matrix / Agents /
  //    Audit) + check tester. If no RD is picked yet, just "Your RDs" + check.
  const visibleNav = canPlatform
    ? NAV_ITEMS.filter(i => !i.platformOnly || canPlatform)
    : [
        { label: 'Your Research Domains', path: '/rd-home', icon: <BusinessIcon fontSize="small" /> },
        ...(currentRdId ? [
          { label: 'RD Overview', path: `/rds/${currentRdId}`, icon: <BusinessIcon fontSize="small" /> },
          { label: 'Policy Matrix', path: `/rds/${currentRdId}/policies`, icon: <SettingsIcon fontSize="small" /> },
          { label: 'Agents', path: `/rds/${currentRdId}/agents`, icon: <CategoryIcon fontSize="small" /> },
          { label: 'Audit Log', path: `/rds/${currentRdId}/audit`, icon: <VerifiedUserIcon fontSize="small" /> },
        ] : []),
        { label: 'Check Tester', path: '/check', icon: <VerifiedUserIcon fontSize="small" /> },
      ]

  function handleDisconnect() {
    localStorage.removeItem('authServiceKey')
    window.location.reload()
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            borderRight: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          },
        }}
      >
        <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>Auth Admin</Typography>
            <Typography variant="caption" color="text.secondary">Rival Platform</Typography>
          </Box>
          <IconButton size="small" onClick={handleDisconnect} title="Disconnect">
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider />

        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Tooltip title="Preview only — filters which screens are shown. NOT a security control: this tool always holds the platform service key. Real RD-Admin access is enforced server-side in Ros." placement="right">
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              View as &nbsp;ⓘ
            </Typography>
          </Tooltip>
          <ToggleButtonGroup
            size="small"
            exclusive
            fullWidth
            value={persona}
            onChange={(_, v) => v && handlePersonaChange(v as Persona)}
          >
            <ToggleButton value="platform" sx={{ textTransform: 'none', fontSize: 12, py: 0.5 }}>Platform Admin</ToggleButton>
            <ToggleButton value="rd" sx={{ textTransform: 'none', fontSize: 12, py: 0.5 }}>RD Admin</ToggleButton>
          </ToggleButtonGroup>
          {persona === 'rd' && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.75, lineHeight: 1.3 }}>
              RD-Admin preview: org/RD creation and global registries are hidden. Open an RD to manage it.
            </Typography>
          )}
        </Box>

        <Divider />

        <List sx={{ px: 1, pt: 1 }}>
          {visibleNav.map(item => {
            const active = item.path === '/rd-home'
              ? location.pathname === '/rd-home'
              : location.pathname === item.path || (item.path !== '/check' && location.pathname.startsWith(item.path + '/'))
            return (
              <ListItemButton
                key={item.path}
                onClick={() => navigate(item.path)}
                sx={{
                  borderRadius: '10px',
                  mb: 0.5,
                  ...(active && {
                    bgcolor: 'action.selected',
                    borderLeft: '3px solid',
                    borderColor: 'primary.main',
                  }),
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: active ? 'primary.main' : 'text.secondary' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: active ? 600 : 400,
                    color: active ? 'text.primary' : 'text.secondary',
                  }}
                />
              </ListItemButton>
            )
          })}
        </List>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: 'background.default', overflow: 'auto' }}>
        {children}
      </Box>
    </Box>
  )
}
